// SellScrapGold.jsx - The page paid search sends "sell scrap gold" to.
//
// The kit form was the wrong place to land a cold click. It has no navigation,
// no footer and no explanation on it, by design, because it is a task rather
// than a document. That is also why Google rated the landing page below average
// and limited the keywords: a stranger arriving from a search has not decided
// anything yet, and neither they nor a crawler could see what this company is.
//
// So this page answers the question that was typed, in the words it was typed
// in, and keeps the kit one click away at the top of the screen.

import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, faqSchema } from "../lib/seo.jsx";
import { KIT } from "../lib/media.js";
import { PageHead, TrustStrip, Steps, FaqList, CtaBand, MobileCta, Figure, Quote, OfferReceipt } from "./parts.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { ArrowRight, Check, Close } from "../ui/icons.jsx";

const COUNTS = [
  ["Snapped and tangled chains", "Knots and missing clasps change nothing. It is weighed as metal."],
  ["Single earrings", "The survivor of a pair is worth exactly half a pair."],
  ["Class rings and signet rings", "Stones come out and go back to you."],
  ["Worn and bent bands", "Thinned wedding bands are still the karat they were made at."],
  ["Dental gold", "Crowns and bridges, with the tooth still attached. We deal with that here."],
  ["Gold watch cases", "The case, not the movement. Send the whole watch if you are unsure."],
];

const NOT_GOLD = [
  ["Gold filled and gold plated", "Marked GF, GP, RGP, HGE or 1/20. The gold is real and microscopic, and it costs more to recover than it is worth."],
  ["Worth more as jewellery", "A signed piece or a good watch. We flag those, send them back, and tell you what you have."],
];

const FAQS = [
  {
    question: "Does broken gold pay less than wearable gold?",
    answer: "No. The offer is worked out from metal content, so a snapped chain and a perfect one of the same weight and karat come out at the same number. Nothing is deducted for condition.",
  },
  {
    question: "Do I need to clean it or sort it first?",
    answer: "No. Send it as it is, mixed karats and all. Every piece is sorted, weighed and tested separately, and each one appears on its own line in your offer.",
  },
  {
    question: "What happens to the stones?",
    answer: "Their weight is deducted so you are not paid for stone as though it were gold, and the stones come back to you. If a piece is worth more as jewellery than as metal, we say so and return it.",
  },
  {
    question: "How do you know what karat it is?",
    answer: "Every piece is tested rather than read off its stamp, because stamps are wrong often enough to matter. The test method and the result are published beside the weight on your tracking page.",
  },
  {
    question: "What if I do not like the offer?",
    answer: "Decline it. Everything comes back insured at our expense, packed as it arrived, usually the next business day. You have 48 hours to answer, and we requote free at the current price after that.",
  },
];

export default function SellScrapGold() {
  return (
    <>
      <Seo
        title="Sell scrap gold by mail"
        path="/sell-scrap-gold"
        description="Sell scrap gold by mail: broken chains, single earrings, class rings and dental gold. Free insured kit both ways, every piece weighed and tested on camera, free return if you decline."
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Sell scrap gold", to: "/sell-scrap-gold" }])} />
      <JsonLd data={faqSchema(FAQS)} />

      <PageHead
        eyebrow="Scrap gold"
        title="We buy scrap gold in any form."
        lede="Broken chains, single earrings, class rings, worn bands and dental gold. We post you a free insured mailer, weigh and test every piece, and send you an itemised offer the day it arrives."
        art={() => (
          <Figure
            src={KIT.mailerTilt}
            alt="The free insured mailer we post out for scrap gold"
            sizes="(min-width: 1024px) 42vw, 88vw"
            priority
          />
        )}
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/get-a-kit" className="btn btn-primary btn-lg">Get a free kit <ArrowRight size={16} /></Link>
          <Link to="/tools/gold-calculator" className="btn btn-quiet btn-lg">Work out what it is worth</Link>
        </div>
        <p className="hint mt-4">Free both ways, no obligation, and your items come back free if you say no.</p>
      </PageHead>

      <TrustStrip className="mt-4" />

      {/* What counts, and what does not. The honest half is why the rest is
          believable, and it saves somebody posting a drawer of plated chain. */}
      <section className="shell band">
        <Reveal>
          <p className="eyebrow mb-3">What counts as scrap gold</p>
          <h2 className="display display-lg text-ink">If it is gold, its shape does not matter.</h2>
          <p className="lede mt-4 measure text-muted">
            Scrap is a trade word for a piece being priced on its metal rather than on what it is. It is not
            a judgement about the piece, and it is not a lower rate.
          </p>
        </Reveal>

        <StaggerBoxed
          as="dl"
          step={70}
          className="grid gap-px bg-rule border border-rule mt-8 sm:grid-cols-2 lg:grid-cols-3"
          itemClassName="bg-paper h-full"
        >
          {COUNTS.map(([title, body]) => (
            <div key={title} className="bg-paper p-5 sm:p-6 h-full">
              <dt className="flex items-start gap-2.5 font-semibold text-[15px] text-ink">
                <Check size={17} className="text-positive shrink-0 mt-0.5" />
                {title}
              </dt>
              <dd className="hint mt-1.5 leading-relaxed">{body}</dd>
            </div>
          ))}
        </StaggerBoxed>

        <Reveal delay={90}>
          <dl className="grid gap-px bg-rule border border-rule mt-6 sm:grid-cols-2">
            {NOT_GOLD.map(([title, body]) => (
              <div key={title} className="bg-surface/60 p-5 sm:p-6">
                <dt className="flex items-start gap-2.5 font-semibold text-[15px] text-ink">
                  <Close size={17} className="text-faint shrink-0 mt-0.5" />
                  {title}
                </dt>
                <dd className="hint mt-1.5 leading-relaxed">{body}</dd>
              </div>
            ))}
          </dl>
          <p className="hint mt-4 measure-wide leading-relaxed">
            Not sure which you have? The{" "}
            <Link to="/tools/karat-chart" className="link">karat and hallmark chart</Link> explains what 417, 585,
            750 and 1/20 mean, and{" "}
            <Link to="/learn/what-does-gold-filled-mean" className="link">what gold filled is worth</Link> covers
            the markings people are caught by most.
          </p>
        </Reveal>
      </section>

      {/* The arithmetic. This is the whole argument, so it gets the receipt. */}
      <section className="band bg-surface/40 border-y border-rule">
        <div className="shell grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 items-center">
          <Reveal>
            <p className="eyebrow mb-3">What the offer is worked out from</p>
            <h2 className="display display-lg text-ink">Weight, purity, and the price that day.</h2>
            <p className="lede mt-4 measure text-muted">
              Every piece is weighed to a hundredth of a gram and tested for purity. Stone weight comes off,
              the pure gold content is totalled, and it is priced at the spot price on the day we quote.
            </p>
            <p className="hint mt-4 measure leading-relaxed">
              All of it goes on your tracking page: the gross weight, the deduction, the test method and the
              result for each piece, next to the video of your package being opened. You can check our
              arithmetic against{" "}
              <Link to="/prices" className="link">today's spot price</Link> before you answer, and against
              anybody else's offer.
            </p>
            <p className="mt-6">
              <Link to="/learn/what-scrap-gold-is-worth" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink link">
                How scrap gold pricing actually works <ArrowRight size={15} />
              </Link>
            </p>
          </Reveal>
          <Reveal delay={120}>
            <OfferReceipt />
          </Reveal>
        </div>
      </section>

      <section className="shell band">
        <Reveal>
          <p className="eyebrow mb-3">How it works</p>
          <h2 className="display display-lg text-ink">Three steps, about a week end to end.</h2>
        </Reveal>
        <Steps className="mt-8" />
      </section>

      <section className="band bg-surface/40 border-y border-rule">
        <div className="shell">
          <Reveal>
            <Quote
              text="The itemised sheet is what did it. Two other places quoted me one number for a whole bag and would not explain it."
              name="Denise O."
              place="Tempe, Arizona"
            />
          </Reveal>
        </div>
      </section>

      <section className="shell band">
        <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-14">
          <Reveal>
            <p className="eyebrow mb-3">Questions</p>
            <h2 className="display display-lg text-ink">The ones people ask first.</h2>
            <p className="hint mt-4 leading-relaxed measure">
              More of them, including the awkward ones, are on the{" "}
              <Link to="/faq" className="link">questions page</Link>.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <FaqList items={FAQS} />
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Send it and see the numbers."
        body="The kit, the postage and the insurance are free in both directions, whether or not you sell."
      />
      <MobileCta />
    </>
  );
}
